import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MdKeyboardArrowDown } from "react-icons/md";
import DashIcon from "components/icons/DashIcon";

export function SidebarLinks({ routes }) {
  const location = useLocation();
  const [open, setOpen] = useState({});

  const isActive = (path) => {
    if (!path) return false;
    const full = `/admin/${path}`;
    return location.pathname === full || location.pathname.startsWith(`${full}/`);
  };

  const isGroupActive = (route) =>
    isActive(route.path) || (route.children || []).some((child) => isGroupActive(child));

  useEffect(() => {
    const next = {};
    routes.forEach((route) => {
      if (route.children?.length && isGroupActive(route)) {
        next[route.name] = true;
      }
    });
    setOpen((prev) => ({ ...prev, ...next }));
  }, [location.pathname, routes]);

  const toggle = (name) => {
    setOpen((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const itemClass = (nested) =>
    `relative mb-3 flex hover:cursor-pointer ${nested ? "ml-4" : ""}`;

  const labelClass = (active) =>
    active
      ? "font-bold text-navy-700 dark:text-white"
      : "font-medium text-gray-600";

  const iconClass = (active) =>
    active ? "font-bold text-[#6A4CF0] dark:text-white" : "font-medium text-gray-600";

  const renderLeaf = (route, index, nested = false) => {
    if (route.hidden || route.layout !== "/admin" || !route.path) return null;
    const active = isActive(route.path);

    return (
      <Link key={`${route.path}-${index}`} to={`${route.layout}/${route.path}`}>
        <div className={itemClass(nested)}>
          <li className={`my-[3px] flex cursor-pointer items-center ${nested ? "px-4" : "px-8"}`}>
            <span className={iconClass(active)}>{route.icon ? route.icon : <DashIcon />}</span>
            <p className={`leading-1 ml-4 flex ${labelClass(active)}`}>{route.name}</p>
          </li>
          {active ? (
            <div className="absolute right-0 top-px h-9 w-1 rounded-lg bg-[#6A4CF0] dark:bg-brand-400" />
          ) : null}
        </div>
      </Link>
    );
  };

  const renderGroup = (route, index) => {
    const groupActive = isGroupActive(route);
    const expanded = !!open[route.name];

    return (
      <div key={`group-${route.name}-${index}`}>
        <button
          type="button"
          onClick={() => toggle(route.name)}
          className="relative mb-3 flex w-full hover:cursor-pointer"
        >
          <li className="my-[3px] flex w-full cursor-pointer items-center px-8">
            <span className={iconClass(groupActive)}>{route.icon ? route.icon : <DashIcon />}</span>
            <p className={`leading-1 ml-4 flex flex-1 text-left ${labelClass(groupActive)}`}>
              {route.name}
            </p>
            <MdKeyboardArrowDown
              className={`h-5 w-5 text-gray-500 transition-transform ${expanded ? "rotate-180" : ""}`}
            />
          </li>
        </button>
        {expanded ? (
          <div className="mb-2 ml-10 border-l border-gray-200 dark:border-white/10">
            {route.children.map((child, childIndex) => renderLeaf(child, childIndex, true))}
          </div>
        ) : null}
      </div>
    );
  };

  return routes.map((route, index) => {
    if (route.hidden) return null;
    if (route.children?.length) return renderGroup(route, index);
    return renderLeaf(route, index);
  });
}

export default SidebarLinks;
